/**
 * DepartmentSection - Customer Group / Department selection
 * Created by neo on 21.01.2026
 */
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useTrans } from '../../Store/System/LocalizationStore';
import { Theme } from '../../Theme/Theme';
import { Button } from '../../Components/Button';
import { CustomerGroup, CustomerGroupApi } from '../../Model/CustomerGroup/CustomerGroup';

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SettingsCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const CardHeader = styled.div`
  margin-bottom: 20px;
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 4px 0;
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

const DepartmentGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DepartmentOption = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 2px solid ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lighterGrey)};
  border-radius: 10px;
  background: ${(props) => (props.$selected ? `${Theme.Colors.primary}08` : 'white')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    border-color: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightGrey)};
    background: ${(props) => (props.$selected ? `${Theme.Colors.primary}08` : Theme.Colors.lightestGrey)};
  }
`;

const DepartmentIcon = styled.div<{ $selected?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightestGrey)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    color: ${(props) => (props.$selected ? 'white' : Theme.Colors.warmGrey)};
  }
`;

const DepartmentContent = styled.div`
  flex: 1;
`;

const DepartmentName = styled.div<{ $selected?: boolean }>`
  font-size: 15px;
  font-weight: ${(props) => (props.$selected ? '600' : '400')};
  color: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.textColor)};
`;

const DepartmentDescription = styled.div`
  font-size: 12px;
  color: ${Theme.Colors.charcoal};
  margin-top: 2px;
`;

const RadioIndicator = styled.div<{ $selected?: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 11px;
  border: 2px solid ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightGrey)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background: ${(props) => (props.$selected ? Theme.Colors.primary : 'transparent')};
  }
`;

const SaveButton = styled(Button)`
  margin-top: 24px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
`;

const EmptyMessage = styled.p`
  color: ${Theme.Colors.charcoal};
  text-align: center;
  padding: 20px;
  margin: 0;
`;

const SuccessMessage = styled.div`
  color: ${Theme.Colors.primary};
  font-size: 14px;
  margin-top: 8px;
`;

const GroupIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export type DepartmentSectionProps = {
  onHide?: () => void;
};

export const DepartmentSection: React.FC<DepartmentSectionProps> = observer(({ onHide }) => {
  const { t } = useTrans();

  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>(undefined);

  useEffect(() => {
    Promise.all([CustomerGroupApi.list(), CustomerGroupApi.current()])
      .then(([groups, currentGroup]) => {
        setCustomerGroups(groups);
        if (currentGroup) {
          setSelectedGroupId(currentGroup.id);
        }
        if (groups.length === 0 && onHide) {
          onHide();
        }
      })
      .finally(() => setLoading(false));
  }, [onHide]);

  const handleSelectGroup = useCallback((groupId: string) => {
    setSelectedGroupId(groupId);
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedGroupId) return;
    setSaving(true);
    setSaved(false);
    try {
      await CustomerGroupApi.setCurrent(selectedGroupId);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving department:', error);
    } finally {
      setSaving(false);
    }
  }, [selectedGroupId]);

  if (loading) {
    return (
      <SectionContent>
        <SettingsCard>
          <CardHeader>
            <CardTitle>{t('profile.settings.customerGroup', { defaultValue: 'Department' })}</CardTitle>
          </CardHeader>
          <LoadingContainer>
            {t('accountScreen.department.loading', { defaultValue: 'Loading departments...' })}
          </LoadingContainer>
        </SettingsCard>
      </SectionContent>
    );
  }

  if (customerGroups.length === 0) {
    return null;
  }

  return (
    <SectionContent>
      <SettingsCard>
        <CardHeader>
          <CardTitle>{t('profile.settings.customerGroup', { defaultValue: 'Department' })}</CardTitle>
          <CardDescription>
            {t('accountScreen.department.description', {
              defaultValue: 'Select the department you belong to within your organization.',
            })}
          </CardDescription>
        </CardHeader>

        <DepartmentGrid>
          {customerGroups.map((group) => {
            const selected = selectedGroupId === group.id;
            return (
              <DepartmentOption key={group.id} $selected={selected} onClick={() => handleSelectGroup(group.id)}>
                <DepartmentIcon $selected={selected}>
                  <GroupIcon />
                </DepartmentIcon>
                <DepartmentContent>
                  <DepartmentName $selected={selected}>{group.name}</DepartmentName>
                  {group.description && <DepartmentDescription>{group.description}</DepartmentDescription>}
                </DepartmentContent>
                <RadioIndicator $selected={selected} />
              </DepartmentOption>
            );
          })}
        </DepartmentGrid>

        <SaveButton color="primary" size="sm" onClick={handleSave} disabled={saving || !selectedGroupId}>
          {saving
            ? t('common.saving', { defaultValue: 'Saving...' })
            : t('editProfileScreen.saveChanges', { defaultValue: 'Save Changes' })}
        </SaveButton>
        {saved && (
          <SuccessMessage>
            {t('accountScreen.department.success', { defaultValue: 'Department updated successfully!' })}
          </SuccessMessage>
        )}
      </SettingsCard>
    </SectionContent>
  );
});
